/*
  Product add-ons.

  Two deliberate details:

  1) Cart submission. Dawn's product-form.js already owns the submit event and
     builds FormData from the form. A second request would race it — with
     cart_type "page" Dawn redirects to /cart on success, cancelling anything
     still in flight. So instead we rewrite the form's fields into the Ajax
     API's items[] shape during the CAPTURE phase, before Dawn's own listener.
     Dawn then sends ONE request with both lines and keeps its error handling.

  2) Images. Both add-on cards render up front and are shown/hidden on variant
     change. Card images are eager (a lazy image inside a hidden element isn't
     fetched until shown, which flashed on the first switch); the larger
     lightbox images are warmed in the background once the page has loaded.
*/
(function () {
  /* Dawn renders the quantity input OUTSIDE the <form>, associated to it with a
     `form="..."` attribute. It is therefore in form.elements (and in FormData)
     but NOT a descendant — form.querySelector('[name=quantity]') finds nothing. */
  function productForm() {
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }
  function field(form, name) {
    if (!form) return null;
    const el = form.elements ? form.elements[name] : null;
    if (el && el.length && !el.value) return el[0];
    return el || form.querySelector('[name="' + name + '"]');
  }

  /* A match rule is a comma-separated AND list: "6ft, standard" applies only
     when BOTH words appear. This is how 6ft Premium is excluded while 7ft
     stays open to both branch types — a single substring cannot express that.
     One term behaves exactly as before, so existing rules keep working. */
  /* Merchant copy is written in the Shopify editor, which turns ' into a
     curly quote. Normalise both sides so "WHAT'S INCLUDED" matches whichever
     apostrophe the copy actually uses. */
  function plain(text) {
    return String(text || '').replace(/[\u2018\u2019\u02BC]/g, "'").toLowerCase();
  }

  /* Stephen asked the popup to skip the intro and open straight at the
     "what's included" list: the teaser on the card already carries the intro,
     so repeating it in the popup was just more to scroll past. Everything
     before the marker's own element is dropped. If the marker is missing or
     blank the whole description is shown, so editing the copy can never leave
     the popup empty. */
  function trimToStart(node, marker) {
    const needle = plain(marker).trim();
    if (!needle) return node;
    const kids = Array.from(node.children);
    const at = kids.findIndex((el) => plain(el.textContent).indexOf(needle) !== -1);
    if (at <= 0) return node;
    kids.slice(0, at).forEach((el) => el.remove());
    return node;
  }

  /* The delivery month is attached to the TREE line as a line item property,
     so it reaches the cart, checkout, order and packing slip — Shopify has no
     delivery-date field of its own, and the variant name can't say "October"
     for the add-on buyers and "November" for everyone else at the same time.

     Rules are "words the variant must contain | text", most specific first,
     because 7ft Premium ships split while the Standard sizes ship complete. */
  function deliveryConfig(el) {
    const d = (el && el.dataset) || {};
    return {
      name: d.deliveryProperty || 'Delivery',
      fallback: d.deliveryDefault || '',
      rules: String(d.deliveryRules || '')
        .split('\n')
        .map((line) => {
          const at = line.indexOf('|');
          if (at === -1) return null;
          return { match: line.slice(0, at).trim(), text: line.slice(at + 1).trim() };
        })
        .filter((r) => r && r.text),
    };
  }

  function deliveryText(cfg, variantHaystack, hasAddon) {
    if (!hasAddon) return cfg.fallback;
    const hit = cfg.rules.find((r) => matchesRule(variantHaystack, r.match));
    return hit ? hit.text : cfg.fallback;
  }

  function matchesRule(haystack, rule) {
    const terms = String(rule || '')
      .toLowerCase()
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (!terms.length) return true;
    return terms.every((t) => haystack.indexOf(t) !== -1);
  }

  class ProductAddons extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      this.sectionId = this.dataset.sectionId;
      this.cards = Array.from(this.querySelectorAll('[data-mw-addon]'));

      this.bindCards();
      this.syncToVariant(this.currentVariant());
      this.warmImages();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const d = event && event.data;
          if (!d || (d.sectionId && this.sectionId && d.sectionId !== this.sectionId)) return;
          this.syncToVariant(d.variant || null);
        });
        // Dawn publishes variantChange only after it has fetched and applied
        // the new section HTML, so on a slow connection the selector would lag
        // the click by a second or more — and if that update fails partway
        // (a media block throwing, say) it never fires at all. The option
        // change itself is published synchronously on click, and the checked
        // radios already describe the new variant, so sync from those first.
        if (PUB_SUB_EVENTS.optionValueSelectionChange) {
          this.unsubscribeOption = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
            const d = event && event.data;
            if (d && d.sectionId && this.sectionId && d.sectionId !== this.sectionId) return;
            this.syncToVariant(null);
          });
        }
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.unsubscribeOption) this.unsubscribeOption();
    }

    /* Warm the full-size images so the popup opens instantly. Card images are
       already eager; this runs after load so it never competes with them. */
    warmImages() {
      const run = () => {
        this.cards.forEach((card) => {
          galleryFor(card).forEach((url) => {
            const i = new Image();
            i.decoding = 'async';
            i.src = url;
          });
          const img = card.querySelector('.mw-addon__img');
          if (img && img.currentSrc === '' && img.src) { const i = new Image(); i.src = img.src; }
        });
      };
      if (document.readyState === 'complete') setTimeout(run, 200);
      else window.addEventListener('load', () => setTimeout(run, 200), { once: true });
    }

    currentVariant() {
      try {
        const node = document.querySelector('product-info [data-selected-variant]')
          || document.querySelector('[data-selected-variant]');
        if (node) return JSON.parse(node.textContent);
      } catch (e) { /* fall through */ }
      return null;
    }

    variantText(variant) {
      if (variant) return [variant.title, (variant.options || []).join(' ')].join(' ').toLowerCase();
      const picked = Array.from(
        document.querySelectorAll('variant-selects input:checked, variant-selects option:checked, variant-radios input:checked')
      ).map((n) => n.value || n.textContent || '');
      return picked.join(' ').toLowerCase();
    }

    syncToVariant(variant) {
      const text = this.variantText(variant);
      const cfg = deliveryConfig(this);
      let visible = null;

      this.cards.forEach((card) => {
        const applies = matchesRule(text, card.dataset.match);
        card.hidden = !applies;
        const box = card.querySelector('[data-mw-check]');
        // a hidden add-on must never reach the cart
        if (!applies && box && box.checked) { box.checked = false; box.dispatchEvent(new Event('change', { bubbles: true })); }
        if (applies && !visible) visible = card;
      });

      // The option's delivery line is the same text stamped on the add-on's
      // cart line (snippets/addon-notice), so what it promises is exactly
      // what checkout will say.
      if (visible) {
        const sub = visible.querySelector('[data-mw-sub]');
        if (sub) {
          // Low stock (decided server-side from inventory) replaces the line.
          sub.textContent = visible.dataset.lowStock === 'true' && visible.dataset.lowLabel
            ? visible.dataset.lowLabel
            : this.arrives(this.addonDelivery());
        }
      }

      // No add-on for this variant (6ft Premium): nothing to offer, so the
      // whole box steps out of the page.
      this.hidden = !visible;
      const banner = this.querySelector('[data-mw-details]');
      if (banner) banner.hidden = !visible;
      const notice = this.querySelector('[data-mw-notice]');
      if (notice) notice.hidden = !visible;
    }

    /* The add-on's delivery line right now: the short notice while it runs,
       the after-text once it has ended. Decided server-side on each render. */
    addonDelivery() {
      return String(this.dataset.addonDelivery || '').trim();
    }

    /* "November" -> "Arrives in November"; a longer text such as the split
       delivery is shown as written, since it already reads as a sentence. */
    arrives(text) {
      const t = String(text || '').trim();
      if (!t) return '';
      const tpl = this.dataset.arrivesTemplate || 'Arrives in [month]';
      return /\s/.test(t) ? t : tpl.replace('[month]', t);
    }

    bindCards() {
      const banner = this.querySelector('[data-mw-details]');
      if (!banner) return;
      banner.addEventListener('click', (e) => {
        e.preventDefault();
        const card = this.cards.find((c) => !c.hidden);
        if (card) openModal(card);
      });
    }

    /* How many trees is the customer buying right now? */
    treeQty() {
      const input = field(productForm(), 'quantity');
      return Math.max(1, parseInt(input && input.value, 10) || 1);
    }

    /* Variant text for a tree variant id, from the map the snippet emits. */
    textForId(id) {
      let list = [];
      try { list = JSON.parse(this.dataset.variants || '[]'); } catch (e) { list = []; }
      const hit = list.find((v) => String(v.id) === String(id));
      return hit ? String(hit.title).toLowerCase() : '';
    }

    /* Did the customer tick the add-on? Only the visible card's box can be
       checked (hidden cards are cleared on variant change). */
    bundleWanted() {
      return this.cards.some((c) => {
        const r = c.querySelector('[data-mw-check]');
        return r && r.checked;
      });
    }

    /* The add-on line to add, one per tree. When the submitting variant's
       text is given, the add-on is matched to THAT variant rather than to the
       option on screen — Dawn only updates the form's variant id after its
       section fetch, so a click mid-switch would otherwise pair a 4ft theme
       with a 6ft tree. Falls back to the visible option. */
    chosenCard(forText) {
      if (!this.bundleWanted()) return null;
      return (forText
        ? this.cards.find((c) => c.dataset.available === 'true' && matchesRule(forText, c.dataset.match))
        : this.cards.find((c) => !c.hidden && c.dataset.available === 'true')) || null;
    }

    selected(forText) {
      const card = this.chosenCard(forText);
      return card ? [{ id: card.dataset.variantId, quantity: this.treeQty() }] : [];
    }
  }

  /* ---------------- modal (image zoom + details) ----------------
     One popup for both triggers — the photo and "View details" open the same
     view, which is what the old app did. The description is cloned out of the
     card rather than rendered a second time, so there is only ever one copy of
     the copy. */
  let modal;

  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'mw-modal';
    modal.innerHTML =
      '<div class="mw-modal__box" role="dialog" aria-modal="true">' +
        '<button type="button" class="mw-modal__close" aria-label="Close">&times;</button>' +
        '<div class="mw-modal__media">' +
          '<img class="mw-modal__photo" alt="">' +
          '<div class="mw-modal__thumbs" data-mw-thumbs></div>' +
        '</div>' +
        '<div class="mw-modal__body">' +
          '<p class="mw-modal__title"></p>' +
          '<p class="mw-modal__price"></p>' +
          '<div class="mw-modal__desc"></div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.classList.contains('mw-modal__close')) closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
    return modal;
  }

  /* Read the card's image list. Falls back to the card thumbnail so a card
     that somehow has no gallery still shows its photo rather than a blank. */
  function galleryFor(card) {
    let urls = [];
    try { urls = JSON.parse(card.dataset.gallery || '[]'); } catch (e) { urls = []; }
    urls = urls.filter(Boolean);
    if (!urls.length) {
      const img = card.querySelector('.mw-addon__img');
      if (img && img.src) urls = [img.src];
    }
    return urls;
  }

  function openModal(card) {
    const m = ensureModal();
    const photo = m.querySelector('.mw-modal__photo');
    const thumbs = m.querySelector('[data-mw-thumbs]');
    const urls = galleryFor(card);

    photo.src = urls[0] || '';
    photo.alt = (card.querySelector('.mw-addon__title') || {}).textContent || '';

    /* One photo needs no picker — the strip would just be a single thumbnail
       repeating the image above it. */
    thumbs.innerHTML = '';
    thumbs.hidden = urls.length < 2;
    urls.forEach((url, index) => {
      const t = document.createElement('button');
      t.type = 'button';
      t.className = 'mw-modal__thumb' + (index === 0 ? ' is-active' : '');
      t.setAttribute('aria-label', 'View image ' + (index + 1));
      t.innerHTML = '<img alt="" src="' + url + '">';
      t.addEventListener('click', () => {
        photo.src = url;
        thumbs.querySelectorAll('.mw-modal__thumb').forEach((el) => el.classList.remove('is-active'));
        t.classList.add('is-active');
      });
      thumbs.appendChild(t);
    });

    m.querySelector('.mw-modal__title').textContent =
      (card.querySelector('.mw-addon__title') || {}).textContent || '';
    m.querySelector('.mw-modal__price').textContent =
      (card.querySelector('.mw-addon__price') || {}).textContent || '';

    const desc = card.querySelector('[data-mw-desc]');
    const target = m.querySelector('.mw-modal__desc');
    target.innerHTML = '';
    const host = card.closest('product-addons');
    const noticeText = host ? String(host.dataset.notice || '').trim() : '';
    if (noticeText) {
      const n = document.createElement('p');
      n.className = 'mw-modal__notice';
      n.textContent = noticeText;
      target.appendChild(n);
    }
    if (desc) {
      const copy = desc.cloneNode(true);
      copy.removeAttribute('data-mw-desc');
        copy.removeAttribute('hidden');   // hidden in the card, shown in the popup
      copy.className = '';           // drop the two-line clamp
      const widget = card.closest('product-addons');
      trimToStart(copy, widget && widget.dataset.detailsStart);
      target.appendChild(copy);
    }

    m.querySelector('.mw-modal__box').scrollTop = 0;
    m.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const close = m.querySelector('.mw-modal__close');
    if (close) close.focus();
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* ---------------- cart submission ---------------- */
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!form || form.dataset.type !== 'add-to-cart-form') return;

      const widget = document.querySelector('product-addons');
      if (!widget || typeof widget.selected !== 'function') return;

      const idInput = field(form, 'id');
      const qtyInput = field(form, 'quantity');
      if (!idInput || !idInput.value) return;

      // Everything below keys off the variant the form is about to submit.
      const text = widget.textForId(idInput.value) || widget.variantText(widget.currentVariant());
      const addons = widget.selected(text);

      const cfg = deliveryConfig(widget);
      const chosen = widget.chosenCard(text);
      // Tree line: the delivery rules (now just the default month).
      const note = !cfg.fallback ? '' : deliveryText(cfg, text, addons.length > 0);
      // Add-on line: the notice while it runs, the after-text once it ends,
      // or the low-stock wording when that applies.
      const addonNote = (chosen && chosen.dataset.lowStock === 'true' && chosen.dataset.lowDelivery)
        ? chosen.dataset.lowDelivery
        : widget.addonDelivery();

      const injected = [];

      if (!addons.length) {
        // No add-on: Dawn's own single-line submit is fine, the tree just needs
        // to carry the default delivery note with it.
        if (note) injected.push(hidden(form, 'properties[' + cfg.name + ']', note));
        if (injected.length) {
          setTimeout(() => injected.forEach((n) => n.remove()), 0);
        }
        return;
      }

      const mainQty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      const lines = [{ id: idInput.value, quantity: mainQty }].concat(addons);

      lines.forEach((line, i) => {
        injected.push(hidden(form, 'items[' + i + '][id]', line.id));
        injected.push(hidden(form, 'items[' + i + '][quantity]', line.quantity));
      });
      // Each line carries its own note: the tree its month, the add-on the notice.
      if (note) injected.push(hidden(form, 'items[0][properties][' + cfg.name + ']', note));
      if (addonNote) {
        addons.forEach((a, i) => injected.push(hidden(form, 'items[' + (i + 1) + '][properties][' + cfg.name + ']', addonNote)));
      }
      idInput.disabled = true;
      if (qtyInput) qtyInput.disabled = true;

      // Dawn reads its FormData synchronously in the handler that runs right
      // after this one, so restoring next tick is safe and leaves the form
      // intact if the request fails.
      setTimeout(() => {
        injected.forEach((n) => n.remove());
        idInput.disabled = false;
        if (qtyInput) qtyInput.disabled = false;
      }, 0);
    },
    true
  );

  function hidden(form, name, value) {
    const i = document.createElement('input');
    i.type = 'hidden';
    i.name = name;
    i.value = value;
    form.appendChild(i);
    return i;
  }

  /* ---------------- cart page: upsell + enforcement ---------------- */

  /* The add-on line's own delivery note, from the cart widget's data. */
  function addonProps(widget) {
    const d = (widget && widget.dataset) || {};
    const text = String(d.addonDelivery || '').trim();
    if (!text) return {};
    return Object.fromEntries([[d.deliveryProperty || 'Delivery', text]]);
  }
  const CART_NOTE_KEY = 'mw-addon-cart-note';

  /* Shown on its own, NOT from inside <cart-addons>: if the last tree goes and
     its add-on is removed with it the cart is empty, the widget never renders,
     and a notice tied to it would never appear. */
  function showCartNote() {
    let note;
    try {
      note = sessionStorage.getItem(CART_NOTE_KEY);
      if (note) sessionStorage.removeItem(CART_NOTE_KEY);
    } catch (e) { return; }
    if (!note) return;

    // Dawn hides .cart__contents when the cart is empty and .cart__warnings
    // when it isn't — so the note has to go wherever is actually on screen.
    const root = document.querySelector('cart-items');
    const isEmpty = !!root && root.classList.contains('is-empty');
    const host = (isEmpty
        ? document.querySelector('.cart__warnings')
        : document.getElementById('main-cart-items'))
      || document.querySelector('.cart__warnings')
      || document.getElementById('main-cart-items')
      || document.querySelector('main');
    if (!host) return;

    const el = document.createElement('p');
    el.className = 'mw-cartaddons__note';
    el.setAttribute('role', 'status');
    el.textContent = note;
    host.insertBefore(el, host.firstChild);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showCartNote, { once: true });
  } else {
    showCartNote();
  }

  class CartAddons extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      this.bindCards();
      this.enforce();

      // Re-check whenever the cart changes (line removed, quantity edited).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(() => this.enforce(), 120));
      }
    }

    disconnectedCallback() { if (this.unsub) this.unsub(); }

    bindCards() {
      this.querySelectorAll('[data-mw-cart-addon]').forEach((card) => {
        const input = card.querySelector('[data-mw-qty-input]');
        const minus = card.querySelector('[data-mw-minus]');
        const plus = card.querySelector('[data-mw-plus]');
        const cap = card.querySelector('[data-mw-cap]');
        const max = Math.max(1, parseInt(card.dataset.max, 10) || 1);

        const sync = () => {
          const v = Math.min(Math.max(1, parseInt(input.value, 10) || 1), max);
          input.value = v;
          plus.disabled = v >= max;
          minus.disabled = v <= 1;
          if (cap) {
            cap.textContent = v >= max
              ? 'Maximum ' + max + ' — one per tree in your cart.'
              : 'You can add up to ' + max + ' (one per tree).';
            cap.classList.toggle('is-hit', v >= max);
          }
        };
        minus.addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) - 1; sync(); });
        plus.addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) + 1; sync(); });
        input.addEventListener('change', sync);
        sync();

        card.querySelector('[data-mw-cart-add]').addEventListener('click', (e) => {
          const btn = e.currentTarget;
          btn.setAttribute('aria-busy', 'true');
          const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
          fetch(url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{
              id: Number(card.dataset.variantId),
              quantity: Math.min(max, parseInt(input.value, 10) || 1),
              properties: addonProps(card.closest('cart-addons')),
            }] }),
          })
            .then((r) => r.json())
            .then((data) => {
              if (data && data.status) throw new Error(data.description || data.message || 'Cart error');
              window.location.reload();
            })
            .catch((err) => {
              btn.setAttribute('aria-busy', 'false');
              console.warn('[cart-addons] add failed:', err);
            });
        });
      });
    }

    rules() {
      try { return JSON.parse(this.dataset.rules || '[]'); } catch (e) { return []; }
    }

    /* How many trees back each rule, from a fresh cart. */
    treeCounts(cart) {
      const counts = {};
      this.rules().forEach((rule) => {
        if (!String(rule.match || '').trim()) return;
        let trees = 0;
        cart.items.forEach((line) => {
          if (line.product_id === rule.productId) return;
          const hay = (line.product_title + ' ' + (line.variant_title || '')).toLowerCase();
          if (matchesRule(hay, rule.match)) trees += line.quantity;
        });
        counts[rule.productId] = trees;
      });
      return counts;
    }

    /* The tree's delivery note has to follow the add-on. Remove the Halloween
       theme at the cart and the tree must go back to the default month,
       otherwise the order promises October and ships in November. */
    syncDelivery(cart) {
      const cfg = deliveryConfig(this);
      if (!cfg.fallback || this.busy) return;

      const rules = this.rules();
      const addonIds = new Set(rules.map((r) => r.productId));
      // "productId|text": while that add-on is low on stock, its trees carry
      // this note instead of the month (same decision the product page made).
      const ov = String(this.dataset.deliveryOverride || '');
      const ovId = ov.indexOf('|') !== -1 ? Number(ov.slice(0, ov.indexOf('|'))) : null;
      const ovText = ovId !== null ? ov.slice(ov.indexOf('|') + 1) : '';
      const wanted = (rule, hay, hasAddon) =>
        hasAddon && rule.productId === ovId && ovText ? ovText : deliveryText(cfg, hay, hasAddon);
      const fixes = [];

      const addonWant = String(this.dataset.addonDelivery || '').trim();

      cart.items.forEach((line) => {
        if (addonIds.has(line.product_id)) {
          // The add-on line carries the notice / after-text of the day.
          const haveA = (line.properties || {})[cfg.name] || '';
          if (addonWant && haveA !== addonWant) fixes.push({ key: line.key, quantity: line.quantity, want: addonWant });
          return;
        }
        const hay = (line.product_title + ' ' + (line.variant_title || '')).toLowerCase();
        const rule = rules.find((r) => matchesRule(hay, r.match));
        if (!rule) return;                                   // not a tree we manage

        const hasAddon = cart.items.some(
          (l) => l.product_id === rule.productId && l.quantity > 0
        );
        const want = wanted(rule, hay, hasAddon);
        const have = (line.properties || {})[cfg.name] || '';
        if (want !== have) fixes.push({ key: line.key, quantity: line.quantity, want: want });
      });

      if (!fixes.length) return;
      this.busy = true;

      // change.js is one line at a time; these carts are two or three lines.
      const run = fixes.reduce(
        (chain, fix) =>
          chain.then(() =>
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({
                id: fix.key,
                quantity: fix.quantity,
                properties: Object.fromEntries([[cfg.name, fix.want]]),
              }),
            }).then((r) => r.json())
          ),
        Promise.resolve()
      );

      run
        .then((finalCart) => {
          // Only refresh once the cart confirms the new value, so a failed
          // write can never put us in a reload loop.
          const ok = (finalCart.items || []).every((l) => {
            if (addonIds.has(l.product_id)) return !addonWant || ((l.properties || {})[cfg.name] || '') === addonWant;
            const hay = (l.product_title + ' ' + (l.variant_title || '')).toLowerCase();
            const rule = rules.find((r) => matchesRule(hay, r.match));
            if (!rule) return true;
            const hasAddon = (finalCart.items || []).some(
              (x) => x.product_id === rule.productId && x.quantity > 0
            );
            return ((l.properties || {})[cfg.name] || '') === wanted(rule, hay, hasAddon);
          });
          this.busy = false;
          if (ok) window.location.reload();
          else console.warn('[cart-addons] delivery note did not settle; not reloading');
        })
        .catch((err) => {
          this.busy = false;
          console.warn('[cart-addons] delivery sync failed:', err);
        });
    }

    /* An add-on cannot outlive or outnumber its tree. If the tree was removed,
       drop the add-on; if the tree quantity fell, follow it down. */
    enforce() {
      if (!this.rules().length || this.busy) return;

      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          const counts = this.treeCounts(cart);
          const updates = {};
          const removed = [];

          cart.items.forEach((line) => {
            if (!(line.product_id in counts)) return;
            const trees = counts[line.product_id];
            if (line.quantity > trees) {
              updates[line.key] = trees;
              removed.push({ title: line.product_title, to: trees, key: line.key });
            }
          });

          if (!Object.keys(updates).length) {
            this.decorate(cart, counts);
            this.syncDelivery(cart);
            return;
          }

          this.busy = true;

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ updates: updates }),
          })
            .then((r) => r.json())
            .then((updated) => {
              // Only claim something changed once the cart confirms it — writing
              // the note before the request meant a failed update still told the
              // customer their item had been removed.
              const stillThere = new Set((updated.items || []).map((l) => l.key));
              const gone = removed.filter((r) => r.to === 0 && !stillThere.has(r.key));
              const note = gone.length
                ? gone[0].title + ' was removed — it can only be bought with a tree.'
                : 'Add-on quantity updated to match the number of trees in your cart.';
              try { sessionStorage.setItem(CART_NOTE_KEY, note); } catch (e) { /* ignore */ }
              window.location.reload();
            });
        })
        .catch((err) => console.warn('[cart-addons] enforce failed:', err));
    }

    /* Cap the add-on's own cart-line stepper at the tree count. The stepper
       stays visible and keeps Dawn's AJAX behaviour — setting `max` is what
       stops stepUp() going past it, and disabling "+" makes the ceiling
       visible rather than just unresponsive. */
    decorate(cart, counts) {
      cart.items.forEach((line) => {
        if (!(line.product_id in counts)) return;
        const trees = counts[line.product_id];
        const input = document.querySelector('input[data-quantity-variant-id="' + line.variant_id + '"]');
        if (!input) return;

        const widget = input.closest('quantity-input') || input.parentElement;
        widget.hidden = false;

        input.max = trees;
        input.setAttribute('max', trees);

        const plus = widget.querySelector('button[name="plus"]');
        if (plus) {
          const atMax = line.quantity >= trees;
          plus.disabled = atMax;
          plus.classList.toggle('mw-qty-capped', atMax);
          plus.title = atMax ? 'One per tree in your cart' : '';
        }
      });
    }

  }

  if (!customElements.get('product-addons')) customElements.define('product-addons', ProductAddons);
  if (!customElements.get('cart-addons')) customElements.define('cart-addons', CartAddons);
})();
